import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { FlexDatesRoundTrip, getQualifiedFlexDates } from '@pcln/fly-listings'
import InfiniteScroll from 'react-infinite-scroll-component'
import styled from 'styled-components'
import get from 'lodash/get'
import NoSSR from 'react-no-ssr'
import classNames from 'classnames'
import { detect } from 'detect-browser'
import { useFeature } from '@pcln/feature-components'
import { useSelector } from 'react-redux'
import { Box } from 'pcln-design-system'
import moment from 'moment'

import { pclnExpt as flyExpt } from '../../../common/fly-experimentation/fly-experimentation'
import { ListingsComponentWrapper } from '../ListingsComponentWrapper/ListingsComponentWrapper'
import { AdContainer } from '../../containers/ad-container'
import { ErrorOverlay } from '../ErrorOverlay'
import { BaggageFeeOverlay } from '../BaggageFeeOverlay'
import { LoaderContainer } from '../Loader/LoaderContainer'
import { SearchErrorScreen } from '../SearchErrorScreen'
import { FlexDatesTilesContainer } from '../FlexDatesTiles'
import { getFlexDates } from '../FlexDatesTiles/helper'
import { BackToTop } from '../BackToTop'
import { LoadMoreBtn } from '../LoadMoreBtn'
import { LoadingText } from '../LoadingText'
import { ThrottledTripsBanner } from '../ThrottledTripsBanner'
import { OverfilteredBannerContainer } from '../OverfilteredBanner/OverfilteredBanner'
import { ViewersFlyIn } from '../ViewersFlyIn'
import { userLoginData } from '../../util/userLoginData'
import { getDeviceWidth } from '../../util/displayHelpers'
import { showPhoneSalesBanner } from '../../util/phoneSalesBanner/phoneSalesBanner'
import { covidDisplayAttributes } from '../../util/covidDisplayAttributes/covidDisplayAttributes'
import { PhoneSalesBanner } from '../PhoneSalesBanner'
import { getIsShelfEligible } from '../../helpers/helpers'
import { ListingsFlyin } from './ListingsFlyin'
import { PhoneSalesFlyIn } from '../PhoneSalesFlyIn'
import { usePartner } from '@pcln/whitelabel-components'
import { initAffirm } from '../../../common/initAffirm'
import { FlexDatesRoundTripGridWrapper } from './FlexDatesRoundTripGridWrapper'
import { FlexDatesBanner } from '@pcln/fly-ui-components'
import { logCarouselArrow } from '../../../../client/common/log-ga-event/logGaEvent-internal-link.js'
import ReshopHeader from '../ReshopHeader/ReshopHeader'
import { fetchUpsellTrayOptions } from '../../util/fetchUpsellTrayOptions/fetchUpsellTrayOptions'
import {
  logUpsellTrayDisplay,
  logClickShowAddon,
  logTileCount,
} from '../../../../client/common/log-ga-event'

import { gaTagUpsellTileClick } from './helpers.js'
import PCLNAnalytics from '@pcln/analytics'

const InfScrollLoadingText = styled.div`
  height: 36px;
`

/* Disable tap highlight in Chrome to prevent entire listings component from highlighting on touch
   Source: https://stackoverflow.com/a/29961714 */
const StyledListingsWrapper = styled.div`
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  -webkit-tap-highlight-color: transparent;
  display: ${props => (props.hide ? 'none' : 'block')};

  ${props => props.currentMobileFilter && 'visibility: hidden;'}
`

// TODO: Reason for being in global scope
const desktopWidth = 1024
const deviceWidth = getDeviceWidth()
const browser = detect()

function ContentRenderer(props) {
  // Authentication
  const { isUserSignedIn, stickyLoggedIn } = userLoginData
  const isWLPartner = usePartner('isWLPartner') || false
  const flexDatesToggle = useFeature('flights.flexDatesToggle')
  const overFilteredTimeStamp = useSelector(
    state => state?.flags?.overFilteredTimeStamp
  )
  const isReshopExperience = useSelector(
    state => state?.pclnBootstrapData?.isReshopExperience
  )

  const {
    activeTabId,
    affirmEnabled,
    airportToCity,
    brandReference,
    calendarPricing,
    calendarPricingExpressDeals,
    closeBaggageFeeOverlay,
    closeErrorOverlay,
    currentMobileFilter,
    deeplinkModelToken,
    departingFrom,
    destination,
    displayAds,
    displayErrorPage,
    displayFlexDates,
    displayListings,
    displayLoadMore,
    displayNonStopItins,
    exdCarriers,
    featureConfig,
    fetchedInitialListings,
    flexDatesHelperResults,
    flyGaLogic,
    hasUSDestinationsOnly,
    handleDepartureSliceClick,
    handleTabChange,
    isDesktop,
    isSliceSelected,
    isSBS,
    isSBSQL,
    selectedDepartureSummaries,
    listings,
    listingsPropsObject,
    listingsIsLoading,
    listingsMetaData,
    listingsSaleStyle,
    loadMore,
    minRetailPrice,
    minRetailStops,
    nonStopPrice,
    onListingItemSelect,
    onListingsMount,
    onSliceFilter,
    onUpsellSelect,
    origin,
    isIE,
    openBaggageFeesModal,
    pclnGptDartAds,
    perPaxPricingText,
    hasEuropeFlight,
    priceGuidance,
    returnDate,
    returningFrom,
    searchKey,
    searchRefId,
    searchRequest,
    setiDetails,
    showBaggageFeeModal,
    showErrorOverlay,
    showLoadingOverlay,
    showLoadingScreenServerSide,
    slices,
    sponsoredCarrier,
    tabs,
    throttledTrips,
    travelDate,
    forceHeaderOpen,
    tripType,
    xSellAd,
    hasExpressDeal,
    defaultFilterAirports,
    priceBreakersProps,
    cancellationText,
    cancelTextColor,
    phoneBookInfo,
    cancelFooterIcon,
    vipTier,
    showOriginDate,
    enableSOPQ,
    filtersLists,
    enableExpressDealVip,
    isFireFly,
    showFilterChips,
    searchSlices,
  } = props

  const { creditAvailable, changeFee, airlineName } =
    listingsMetaData?.creditAvailableSummary || {}

  useEffect(() => {
    xSellAd && flyExpt.fireImpression('PKG_FE_BUNDLEPROMO_FLISTINGS')
  }, [xSellAd])

  //SETI: AIR_LISTINGS_EXACT_VOID_WINDOW
  const storeFireImpressionStatus = useRef({
    AIR_LISTINGS_EXACT_VOID_WINDOW: false,
    AIR_LISTINGS_MULTIPLE_AIRLINES_LISTINGS: false,
  })
  useEffect(() => {
    if (listings?.length) {
      const checkHourstotriggerSeti = listings.some(
        list => list?.voidWindowInfo?.hoursLeft > 24
      )
      if (
        checkHourstotriggerSeti &&
        !storeFireImpressionStatus.current.AIR_LISTINGS_EXACT_VOID_WINDOW
      ) {
        storeFireImpressionStatus.current.AIR_LISTINGS_EXACT_VOID_WINDOW = true
        flyExpt.fireImpression('AIR_LISTINGS_EXACT_VOID_WINDOW')
      }
      const isMultipleAirlinesPresent = listings.some(
        item => item?.marketingAirlines?.length > 1
      )
      if (
        isMultipleAirlinesPresent &&
        !storeFireImpressionStatus.current
          .AIR_LISTINGS_MULTIPLE_AIRLINES_LISTINGS
      ) {
        storeFireImpressionStatus.current.AIR_LISTINGS_MULTIPLE_AIRLINES_LISTINGS = true
        flyExpt.fireImpression('AIR_LISTINGS_MULTIPLE_AIRLINES_LISTINGS')
      }
    }
  }, [listings])

  /**
   * AIR-46428: Suppress map fly search temporarily
   * https://priceline.atlassian.net/browse/AIR-46428
   *
   * TODO:
   * After flex dates OW redesign wins, we will
   * revisit MapWrapper
   */
  // const MapWrapper = isIE
  //   ? null
  //   : React.lazy(() => import('../MapFlySearch/MapWrapper'))

  // Below no op callback when listing is loading will limit one API call at a time
  // when infinite scroll component call loadMoreItems multiple times
  const loadMoreItems = listingsIsLoading ? () => {} : loadMore

  //Initialize Affirm
  if (affirmEnabled && !isReshopExperience) {
    initAffirm()
  }

  const searchRequestCabinClass = get(
    searchRequest,
    'params["cabin-class"]',
    ''
  )

  const isMultipleAirlinesLogoVariant =
    isDesktop && flyExpt.isVariant('AIR_LISTINGS_MULTIPLE_AIRLINES_LISTINGS')
  const isUpsellTrayTestCriteria =
    isFireFly &&
    isDesktop &&
    tripType === 'OW' &&
    !isReshopExperience &&
    searchRequestCabinClass === 'ECO'
  if (isUpsellTrayTestCriteria) {
    flyExpt.fireImpression('AIR_LISTINGS_UPSELL_TRAY')
  }
  const isUpsellTray =
    isUpsellTrayTestCriteria && flyExpt.isVariant('AIR_LISTINGS_UPSELL_TRAY')

  // Code : GA Tag for upsell tray showaddon and error log

  const gATagUpsell = useRef(isUpsellTray)

  if (gATagUpsell.current) {
    logUpsellTrayDisplay(tripType)
    gATagUpsell.current = false
  }

  const errorLogUpsellCallListings = e => {
    if (gATagUpsell) {
      logTileCount(0)
    }
    PCLNAnalytics.logError({
      error: e,
      message: 'Error log for upsell call on listings' + e.stack,
    })
  }
  // end of code: GA Tag and error log

  const fetchUpsellOptions = async ({ itinerary }) => {
    // GATag for upsell tray
    logClickShowAddon(`Position - ${itinerary.listingPosition}`)
    return await fetchUpsellTrayOptions({
      itinerary,
      searchRequestParams: searchRequest.params,
    })
  }

  const hasUpsellOptions = listingsMetaData.hasUpsellOptions
  if (tripType === 'OW') {
    flyGaLogic.fireGtm(
      'send',
      'event',
      'Mfly-search',
      'Listings Page',
      hasUpsellOptions ? 'Has Upsell Options' : 'No Upsell Options'
    )
  }

  const isShelfEligible = getIsShelfEligible(
    searchRequest,
    hasUSDestinationsOnly,
    tripType,
    hasExpressDeal,
    searchRequestCabinClass,
    get(browser, 'name', ''),
    deviceWidth >= desktopWidth,
    hasUpsellOptions
  )

  const shelfVariantName = get(
    setiDetails,
    'AIR_SEARCH_BRANDED_SHELF_NGS_OW2.variantName',
    'DEFAULT'
  )
  const isShelfVariant =
    isShelfEligible && ['TWOSHELVES', 'THREESHELVES'].includes(shelfVariantName)

  const opacityMaskCss = classNames('', {
    'opacity-mask': !fetchedInitialListings,
  })

  const numAltDates = 3
  const showRoundTripGrid =
    tripType === 'RT' &&
    flyExpt.isVariant('AIR_LISTINGS_FLEX_DATES_REDESIGN_CLICKABLE_CONTAINER')

  const qualifiedFlexDates = getQualifiedFlexDates(
    minRetailPrice,
    calendarPricing,
    travelDate,
    returnDate,
    numAltDates
  )

  // ---- Flex banner impression logic start:
  // Memoize previous flex date price
  // Fire Impression when:
  // 1. prevFlexDatePrice is valid number and
  // 2. prevFlexDatePrice is different from minRetail price
  let searchParams = new URLSearchParams(window.location.search)
  let flexDateParam = atob(
    decodeURIComponent(searchParams.get('prev-flex-date-price'))
  )
  const prevFlexDatePrice = parseInt(flexDateParam, 10)

  const flexDatesDiff = Math.ceil(minRetailPrice) - Math.ceil(prevFlexDatePrice)

  const isValidFlexPrice = !isNaN(prevFlexDatePrice) && !isNaN(minRetailPrice)

  if (isValidFlexPrice && flexDatesDiff !== 0) {
    flyExpt.fireImpression('AIR_LISTINGS_FLEX_PRICE_ALERT')
  }
  // ---- Flex banner impression logic end:

  const willRenderFlexDatesRT =
    calendarPricing &&
    calendarPricing.length > 0 &&
    qualifiedFlexDates &&
    qualifiedFlexDates.length > 0 &&
    (!isSBS || activeTabId === 1)

  /**
   * Fire impression for roundtrip grid redesign
   * AIR_LISTINGS_FLEX_DATES_REDESIGN_CLICKABLE_CONTAINER
   */

  if (
    tripType === 'RT' &&
    qualifiedFlexDates?.length >= 1 && // contains 1-3
    !isFireFly
  ) {
    flyExpt.fireImpression(
      'AIR_LISTINGS_FLEX_DATES_REDESIGN_CLICKABLE_CONTAINER'
    )
  }

  const flexDatesRT = (
    <FlexDatesRoundTrip
      minimumRetailPrice={minRetailPrice}
      qualifiedFlexDates={qualifiedFlexDates}
      flyExpt={flyExpt}
      flyGaLogic={flyGaLogic}
      searchOrigin={origin}
      searchDestination={destination}
      isDesktop={isDesktop}
    />
  )

  const listingsElem = displayListings && (
    <StyledListingsWrapper
      className={opacityMaskCss}
      currentMobileFilter={currentMobileFilter}
      hide={showLoadingOverlay}
    >
      <ListingsComponentWrapper
        {...listingsPropsObject}
        activeTabId={activeTabId}
        anySlicesFilteredFor={isSliceSelected}
        deeplinkModelToken={deeplinkModelToken}
        CTAText={'Choose'}
        departingFrom={departingFrom}
        exdCarriers={exdCarriers}
        featureConfig={featureConfig}
        flyGaLogic={flyGaLogic}
        hasUSDestinationsOnly={hasUSDestinationsOnly}
        handleDepartureSliceClick={handleDepartureSliceClick}
        handleTabChange={handleTabChange}
        tabs={tabs}
        selectedDepartureSummaries={selectedDepartureSummaries}
        listings={listings}
        onListingItemSelect={onListingItemSelect}
        onListingsMount={onListingsMount}
        onSliceFilter={onSliceFilter}
        openBaggageFeesModal={openBaggageFeesModal}
        pclnGptDartAds={pclnGptDartAds}
        listingsSaleStyle={listingsSaleStyle}
        returningFrom={returningFrom}
        searchKey={searchKey}
        searchReferralId={searchRefId}
        SETI={setiDetails}
        showLoadingScreenServerSide={showLoadingScreenServerSide}
        userLoggedIn={isUserSignedIn || stickyLoggedIn}
        sponsoredCarrier={sponsoredCarrier}
        airportToCity={airportToCity}
        minimumRetailPrice={minRetailPrice}
        flexDates={calendarPricing}
        nonStopPrice={nonStopPrice}
        displayNonStopItins={displayNonStopItins}
        brandReference={brandReference}
        pricingDisclosure={perPaxPricingText || ''}
        hasEuropeFlight={hasEuropeFlight}
        slices={slices}
        onUpsellSelect={onUpsellSelect}
        tripType={tripType}
        useRenderInView
        xSellAd={xSellAd}
        searchRequestCabinClass={searchRequestCabinClass}
        hasExpressDeal={hasExpressDeal}
        isShelfEligible={isShelfEligible}
        isSBS={isSBS}
        isSBSQL={isSBSQL}
        listingsMetaData={listingsMetaData}
        priceGuidanceData={priceGuidance}
        priceBreakersProps={priceBreakersProps}
        adsContainer={displayAds ? <AdContainer /> : null}
        cancellationText={cancellationText}
        cancelTextColor={cancelTextColor}
        cancelFooterIcon={cancelFooterIcon}
        covidDisplayAttributes={covidDisplayAttributes}
        vipTier={vipTier}
        showOriginDate={showOriginDate}
        isFireFly={isFireFly}
        enableExpressDealVip={enableExpressDealVip}
        clearFilters={props.clearFilters}
        filtersLists={filtersLists}
        searchRequest={searchRequest}
        showFilterChips={showFilterChips}
        isMultiDestination={tripType === 'MD'}
        searchSlices={searchSlices}
        isReshopExperience={isReshopExperience}
        reshopPerPersonText={'per person with\n credit applied'}
        logCarouselArrow={logCarouselArrow}
        showLoadingOverlay={showLoadingOverlay}
        isMultipleAirlinesLogoVariant={isMultipleAirlinesLogoVariant}
        isUpsellTray={isUpsellTray}
        gaTagUpsellTileClick={gaTagUpsellTileClick}
        errorLogUpsellCallListings={errorLogUpsellCallListings}
        fetchUpsellTrayOptions={fetchUpsellOptions}
      />
    </StyledListingsWrapper>
  )

  /**
   * AIR-46428: Suppress map fly search temporarily
   * https://priceline.atlassian.net/browse/AIR-46428
   *
   * TODO:
   * After flex dates OW redesign wins, we will
   * revisit the map-fly-search design.
   *
   * Once finalized, replace
   * const showMap = false;     with
   * const [showMap, setShowMap] = useState(false);
   *
   */
  const showMap = false

  const showTopXSellAd =
    flyExpt.isVariant('PKG_FE_BUNDLEPROMO_FLISTINGS') && !!xSellAd

  const renderOWflexDates =
    tripType === 'OW' && !!travelDate && moment(travelDate).isValid()

  let oneWayFlexDates = (
    <FlexDatesTilesContainer
      minRetailPrice={minRetailPrice}
      calendarPricing={calendarPricing}
      travelDate={travelDate}
      expressDealInfo={calendarPricingExpressDeals}
      minRetailStops={minRetailStops}
      tripType={tripType}
      searchOrigin={origin}
      searchDestination={destination}
      searchRequestCabinClass={searchRequestCabinClass}
      searchRequest={searchRequest}
      enableSOPQ={enableSOPQ}
    />
  )

  if (calendarPricing?.length > 0) {
    oneWayFlexDates = getFlexDates({
      minRetailPrice,
      minRetailStops,
      calendarPricing,
      travelDate,
      tripType,
      searchOrigin: origin,
      searchDestination: destination,
      expressDealInfo: calendarPricingExpressDeals,
    })
  }

  const showPhoneSalesBannerContent =
    showPhoneSalesBanner(isWLPartner, phoneBookInfo) && !displayErrorPage

  return (
    <div>
      {/* TODO: Do we need this */}
      {showErrorOverlay && <ErrorOverlay onClose={closeErrorOverlay} />}

      {/* Loading overlay */}
      {showLoadingOverlay && <LoaderContainer />}

      {/* Error screen */}
      {displayErrorPage ? (
        <SearchErrorScreen
          forceHeaderOpen={forceHeaderOpen}
          isMobile={!isDesktop}
          errorLinkVariant
          isReshopExperience={isReshopExperience}
          phoneBookNumber={
            isReshopExperience ? '1 (800) 774-2354' : phoneBookInfo?.phoneNumber
          }
        />
      ) : null}

      {isReshopExperience && (
        <ReshopHeader
          airlineName={airlineName}
          creditAvailable={creditAvailable}
          changeFee={changeFee}
        />
      )}

      {/* Baggage fee overlay */}
      <BaggageFeeOverlay
        isOpen={showBaggageFeeModal}
        onClose={closeBaggageFeeOverlay}
        forceiOSOverflowVisible={true}
      />

      {flexDatesHelperResults &&
        flexDatesHelperResults.isFlexDatesFlyin &&
        !showPhoneSalesBanner(isWLPartner, phoneBookInfo) && (
          <ListingsFlyin
            flyinText={flexDatesHelperResults.flexDatesFlyinText}
            flyinText2={flexDatesHelperResults.flexDatesFlyinText2}
            flyinImage={flexDatesHelperResults.flexDatesFlyinImage}
            flyinLink={flexDatesHelperResults.flexDatesFlyinLink}
          />
        )}

      {!isReshopExperience &&
      !isWLPartner &&
      !showPhoneSalesBanner(isWLPartner, phoneBookInfo) &&
      tripType !== 'MD' &&
      defaultFilterAirports &&
      flexDatesToggle ? (
        <NoSSR>
          <ViewersFlyIn
            defaultFilterAirports={defaultFilterAirports}
            returningFrom={returningFrom}
            travelDate={travelDate}
            isIE={isIE}
          />
        </NoSSR>
      ) : null}

      {showPhoneSalesBannerContent ? (
        <NoSSR>
          <PhoneSalesFlyIn
            isDesktop={isDesktop}
            phoneBookInfo={phoneBookInfo}
            searchRefId={searchRefId}
          />
        </NoSSR>
      ) : null}

      <OverfilteredBannerContainer setiDetails={setiDetails} />

      {showPhoneSalesBannerContent && (
        <PhoneSalesBanner
          isDesktop={isDesktop}
          phoneBookInfo={phoneBookInfo}
          searchRefId={searchRefId}
        />
      )}

      {/*
        // AIR-46428: Suppress map fly search temporarily
       {!isIE && (
        <MapWrapper
          origin={origin}
          destination={destination}
          minRetailPrice={minRetailPrice}
          travelDate={travelDate}
          tripType={tripType}
          flyExpt={flyExpt}
          setShowMap={setShowMap}
          url={window.location.href}
          isMobile={!isDesktop}
        />
      )} */}

      {/* PKG_FE_BUNDLEPROMO_FLISTINGS */
      !isReshopExperience && showTopXSellAd && <Box mb={3}>{xSellAd}</Box>}

      {// AIR_FLEX_DATES_OW_CAROUSEL
      !isReshopExperience &&
        !showRoundTripGrid &&
        !showMap &&
        displayFlexDates &&
        (renderOWflexDates
          ? !!minRetailPrice && !isShelfVariant && oneWayFlexDates
          : willRenderFlexDatesRT && flexDatesRT)}

      {!showLoadingOverlay && displayListings && (
        <div>
          <ThrottledTripsBanner throttledTrips={throttledTrips} />
        </div>
      )}

      {/* AIR_LISTINGS_FLEX_DATES_REDESIGN_CLICKABLE_CONTAINER */}

      {!isReshopExperience && (
        <FlexDatesRoundTripGridWrapper
          showRoundTripGrid={showRoundTripGrid}
          minRetailPrice={minRetailPrice}
          travelDate={travelDate}
          returnDate={returnDate}
          calendarPricing={calendarPricing}
        />
      )}

      {!isReshopExperience && (
        <FlexDatesBanner
          diff={flexDatesDiff}
          isVariant={flyExpt.isVariant('AIR_LISTINGS_FLEX_PRICE_ALERT')}
        />
      )}

      {/* Listings */}
      <InfiniteScroll
        loader={
          displayListings &&
          !showLoadingOverlay && (
            <InfScrollLoadingText>
              <LoadingText />
            </InfScrollLoadingText>
          )
        }
        /* Temporarily disabling the infinite scroll until an alternate library is found */
        hasMore={false}
        next={loadMoreItems}
        style={{ overflow: 'unset !important' }}
      >
        {listingsElem}
      </InfiniteScroll>

      {/* Back to Top */}
      {displayListings && !showLoadingOverlay && <BackToTop />}

      {/* Load More */}
      {!showLoadingOverlay && displayLoadMore && !overFilteredTimeStamp && (
        <LoadMoreBtn
          requestPending={listingsIsLoading}
          onClick={loadMoreItems}
        />
      )}
    </div>
  )
}

ContentRenderer.propTypes = {
  activeTabId: PropTypes.number,
  affirmEnabled: PropTypes.bool,
  airportToCity: PropTypes.any,
  altDeals: PropTypes.array,
  assetURLs: PropTypes.object,
  brandReference: PropTypes.array,
  calendarPricing: PropTypes.array,
  calendarPricingExpressDeals: PropTypes.array,
  cancellationText: PropTypes.func,
  cancelTextColor: PropTypes.string,
  cancelFooterIcon: PropTypes.element,
  closeBaggageFeeOverlay: PropTypes.func.isRequired,
  closeErrorOverlay: PropTypes.func.isRequired,
  currentMobileFilter: PropTypes.string,
  deeplinkModelToken: PropTypes.string,
  departingFrom: PropTypes.object,
  destination: PropTypes.string,
  displayAds: PropTypes.bool,
  displayErrorPage: PropTypes.bool,
  displayFlexDates: PropTypes.bool,
  displayInfiniteScrollLoadMore: PropTypes.bool,
  displayListings: PropTypes.bool,
  displayLoadMore: PropTypes.bool,
  displayNonStopItins: PropTypes.func,
  exdCarriers: PropTypes.array,
  featureConfig: PropTypes.object,
  fetchedInitialListings: PropTypes.bool,
  flexDatesHelperResults: PropTypes.object,
  flyGaLogic: PropTypes.object.isRequired,
  hasUSDestinationsOnly: PropTypes.bool,
  handleDepartureSliceClick: PropTypes.func,
  handleTabChange: PropTypes.func,
  isDesktop: PropTypes.bool,
  isSliceSelected: PropTypes.bool,
  isSBS: PropTypes.bool,
  isSBSQL: PropTypes.bool,
  selectedDepartureSummaries: PropTypes.array,
  listings: PropTypes.array,
  listingsIsLoading: PropTypes.bool,
  listingsLoadingMessage: PropTypes.string,
  listingsMetaData: PropTypes.object,
  listingsSaleStyle: PropTypes.object,
  loadingMessage: PropTypes.string,
  loadMore: PropTypes.func.isRequired,
  minRetailPrice: PropTypes.number,
  minRetailStops: PropTypes.number,
  nonStopPrice: PropTypes.number,
  onListingItemSelect: PropTypes.func.isRequired,
  onListingsMount: PropTypes.func.isRequired,
  onSliceFilter: PropTypes.func.isRequired,
  onUpsellSelect: PropTypes.func.isRequired,
  openBaggageFeesModal: PropTypes.func.isRequired,
  origin: PropTypes.string,
  pclnGptDartAds: PropTypes.array,
  perPaxPricingText: PropTypes.string,
  hasEuropeFlight: PropTypes.bool,
  defaultFilterAirports: PropTypes.array,
  priceGuidance: PropTypes.object,
  isIE: PropTypes.bool,
  phoneBookInfo: PropTypes.object,
  returnDate: PropTypes.string,
  returningFrom: PropTypes.object,
  searchKey: PropTypes.string,
  searchRefId: PropTypes.string,
  searchRequest: PropTypes.object.isRequired,
  enableSOPQ: PropTypes.bool.isRequired,
  SETI: PropTypes.array.isRequired,
  setiDetails: PropTypes.object.isRequired,
  showAceContentMsg: PropTypes.bool,
  showBaggageFeeModal: PropTypes.bool,
  showErrorOverlay: PropTypes.bool,
  showLoadingOverlay: PropTypes.bool,
  showLoadingScreenServerSide: PropTypes.bool,
  slices: PropTypes.array,
  sponsoredCarrier: PropTypes.string,
  stickyLoggedIn: PropTypes.bool,
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      isActive: PropTypes.bool,
      title: PropTypes.string,
      cb: PropTypes.func,
    })
  ),
  throttledTrips: PropTypes.object,
  travelDate: PropTypes.string,
  tripType: PropTypes.string,
  userLoggedIn: PropTypes.bool,
  xSellAd: PropTypes.node,
  hasExpressDeal: PropTypes.bool,
  priceBreakersProps: PropTypes.object,
  listingsPropsObject: PropTypes.object,
  vipTier: PropTypes.string,
  showOriginDate: PropTypes.bool,
  clearFilters: PropTypes.func,
  filtersLists: PropTypes.array,
  enableExpressDealVip: PropTypes.bool,
  isFireFly: PropTypes.bool,
  forceHeaderOpen: PropTypes.func,
  showFilterChips: PropTypes.bool,
  searchSlices: PropTypes.array,
  logCarouselArrow: PropTypes.func,
}

export { ContentRenderer as default } // lazy loaded, keeping default
